import React, { useMemo } from "react";
import { format } from "date-fns";
import { useTimelineContext } from "../context/TimelineContext";
import { calculateScheduledResourcesSummary, calculateStatusChangesSummary, calculatePlanningEventsSummary, calculateHistorySummary, calculateRemindersSummary } from "../utils/summaryCalculations";

export const TimelineFooter: React.FC = () => {
  const { selectedMode, record, timelineData, project, isLoading } = useTimelineContext();

  // Calculate summary based on selected mode
  const summary = useMemo(() => {
    if (!selectedMode || isLoading) return null;

    switch (selectedMode) {
      case "schedule":
        return calculateScheduledResourcesSummary(record, project);
      case "status":
        return calculateStatusChangesSummary(timelineData);
      case "planning":
        return calculatePlanningEventsSummary(timelineData);
      case "history":
        return calculateHistorySummary(timelineData);
      case "reminders":
        return calculateRemindersSummary(timelineData);
      default:
        return null;
    }
  }, [selectedMode, record, timelineData, project, isLoading]);

  // Format summary items with icons
  const formatSummaryItems = () => {
    if (!summary || !selectedMode) return null;

    switch (selectedMode) {
      case "schedule": {
        const schedSummary = summary as ReturnType<typeof calculateScheduledResourcesSummary>;
        if (!schedSummary) return null;
        return (
          <>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-calendar-check text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Days Invested: <strong className="text-foreground font-medium">{schedSummary.daysInvested}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-calendar-days text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Days Projected: <strong className="text-foreground font-medium">{schedSummary.daysProjected}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-users text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Resources: <strong className="text-foreground font-medium">{schedSummary.totalResources}</strong>
              </span>
            </div>
          </>
        );
      }
      case "status": {
        const statusSummary = summary as ReturnType<typeof calculateStatusChangesSummary>;
        if (!statusSummary) return null;
        const lastChangeText = statusSummary.lastChangeDate ? format(new Date(statusSummary.lastChangeDate), "MMM d, yyyy") : "N/A";
        return (
          <>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-list-check text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Total Changes: <strong className="text-foreground font-medium">{statusSummary.totalChanges}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-clock text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Last Change: <strong className="text-foreground font-medium">{lastChangeText}</strong>
              </span>
            </div>
          </>
        );
      }
      case "planning": {
        const planningSummary = summary as ReturnType<typeof calculatePlanningEventsSummary>;
        if (!planningSummary) return null;
        return (
          <>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-diagram-predecessor text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Total Events: <strong className="text-foreground font-medium">{planningSummary.totalEvents}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-calendar-lines text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Days Planned: <strong className="text-foreground font-medium">{planningSummary.totalDaysPlanned}</strong>
              </span>
            </div>
          </>
        );
      }
      case "history": {
        const historySummary = summary as ReturnType<typeof calculateHistorySummary>;
        if (!historySummary) return null;
        const lastActivityText = historySummary.lastActivityDate ? format(new Date(historySummary.lastActivityDate), "MMM d, yyyy") : "N/A";
        return (
          <>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-clock-rotate-left text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Total Events: <strong className="text-foreground font-medium">{historySummary.totalEvents}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-calendar text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Last Activity: <strong className="text-foreground font-medium">{lastActivityText}</strong>
              </span>
            </div>
          </>
        );
      }
      case "reminders": {
        const remindersSummary = summary as ReturnType<typeof calculateRemindersSummary>;
        if (!remindersSummary) return null;
        return (
          <>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-alarm-clock text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Total Reminders: <strong className="text-foreground font-medium">{remindersSummary.totalReminders}</strong>
              </span>
            </div>
            <span className="text-muted-foreground/30 text-[10px]">•</span>
            <div className="flex items-center gap-1.5">
              <i className="fa-light fa-bell text-muted-foreground text-[10px]" />
              <span className="text-[11px] text-muted-foreground">
                Upcoming: <strong className="text-foreground font-medium">{remindersSummary.upcomingReminders}</strong>
              </span>
            </div>
          </>
        );
      }
      default:
        return null;
    }
  };

  const summaryItems = formatSummaryItems();

  if (!summaryItems) return null;

  return (
    <div className="flex-none border-t bg-muted/30 py-2 px-3 sm:px-6">
      <div className="flex items-center justify-center gap-2 flex-wrap">{summaryItems}</div>
    </div>
  );
};
